import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScrollArea } from '@/common/components/ui/scroll-area';
import { useValidation } from '@/policy/contexts';
import { useTableScroll } from '@/policy/hooks/useTableScroll';
import { Section, Warranty } from '@/policy/types/PolicyCoverage';

import { MissingSection } from '../MissingSection/MissingSection';
import { SectionFactory } from './SectionFactory';

interface SectionRendererProps {
  sections: Section[];
  selectedSectionUuid: string | null;
  onSelectionChanged: (sectionUuid: string) => void;
  onAdvance: (sectionUuid: string) => void;
}

export const SectionRenderer = ({
  sections,
  selectedSectionUuid,
  onSelectionChanged,
  onAdvance,
}: SectionRendererProps) => {
  const { t } = useTranslation();
  const { findValidation, handleValidationChange } = useValidation();
  const { tableRefs } = useTableScroll(sections, selectedSectionUuid);
  const [isEditingMissingSection, setIsEditingMissingSection] = useState(false);

  if (sections.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">{t('garantie.noGaranties')}</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full min-h-0">
      <div className="space-y-6 p-6">
        {sections.map((section, index) => {
          const separatorId = `missingSection-${section.identifier}`;
          const nextSection = sections[index + 1];

          const pageAbove =
            section.overlayRectangles[0]?.pageNumber ?? t('common.unknown');
          const pageBelow =
            nextSection?.overlayRectangles[0]?.pageNumber ?? t('common.end');

          const previousSection = index > 0 ? sections[index - 1] : null;
          const shouldShowTitle =
            section.kind !== 'warranty' ||
            previousSection?.kind !== 'warranty' ||
            (section as Warranty).title !== (previousSection as Warranty).title;

          return (
            <Fragment key={section.identifier}>
              <div
                className={`transition-opacity duration-300 ${
                  isEditingMissingSection
                    ? 'pointer-events-none opacity-50'
                    : ''
                }`}
              >
                <SectionFactory
                  section={section}
                  showTitle={shouldShowTitle}
                  selectedSectionUuid={selectedSectionUuid}
                  onSelectionChanged={onSelectionChanged}
                  onAdvance={onAdvance}
                  tableRefs={tableRefs}
                />
              </div>
              <MissingSection
                key={separatorId}
                validation={(() => {
                  const v = findValidation(separatorId);
                  return v?.status !== 'NONE' ? v : undefined;
                })()}
                onOpen={() => onSelectionChanged('')}
                onEditingChange={setIsEditingMissingSection}
                onSave={(description) => {
                  const fullDescription = `[PAGES:${pageAbove}-${pageBelow}] ${description}`;
                  handleValidationChange(separatorId, 'missingSection', {
                    identifier: separatorId,
                    status: 'ERROR',
                    description: fullDescription,
                    confirmed: true,
                  });
                }}
                onDelete={() => {
                  handleValidationChange(separatorId, 'missingSection', {
                    identifier: separatorId,
                    status: 'NONE',
                  });
                }}
              />
            </Fragment>
          );
        })}
      </div>
    </ScrollArea>
  );
};
